<script lang="ts" setup>
import { ChevronDown, LinkExternal02, SearchMd } from "utruckin-icons";
import CommonSearch from "@/components/common/CommonSearch.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import CustomTab from "@/components/custom/CustomTab.vue";
import { useCheckStore } from "@/features/check/check.store";
import { IFilterEligibleTasks } from "@/features/check/check.types";
import { DateTime } from "luxon";
import CommonTable from "@/components/common/CommonTable.vue";
import { useUserStore } from "@/features/users/user.store";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { TaskStatus } from "@/features/tasks/task.enum";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";
import { IWorkType } from "@/features/work-types/work-type.types";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import router from "@/router";

const checkStore = useCheckStore();
const userStore = useUserStore();
const workTypeStore = useWorkTypeStore();
const route = useRoute();

const loading = ref<boolean>(false);
const workTypeLoading = ref<boolean>(false);
const filterPage = ref<number>(1);
const filterPerPage = ref<number>(50);
const eligibleTasks = ref<any[]>([]);
const searchQuery = ref<string>("");
const problem = ref<string>("");
const activeTab = ref<number>(0);
const tabs = [
  { label: "New tasks", value: 0 },
  { label: "Completed", value: 1 },
];
const isAdmin = ref<boolean>(false);
const workTypes = ref<IWorkType[]>([]);

const collapsedDates = ref<Record<string, boolean>>({});
const collapsedShifts = ref<Record<string, boolean>>({});

let searchFilterProblemTimer: ReturnType<typeof setTimeout> | null = null;
let filterTimeout: ReturnType<typeof setTimeout> | null = null;

const groupedByDate = computed(() => {
  const dateMap = new Map<string, Map<string, any[]>>();

  for (const task of eligibleTasks.value) {
    const date = task.created_time
      ? DateTime.fromISO(task.created_time, { zone: "utc" })
          .setZone("America/New_York")
          .toFormat("yyyy-MM-dd")
      : "Unknown";

    const shift = task.shift_name ?? "Unknown Shift";

    if (!dateMap.has(date)) {
      dateMap.set(date, new Map());
    }

    const shiftMap = dateMap.get(date)!;

    if (!shiftMap.has(shift)) {
      shiftMap.set(shift, []);
    }

    shiftMap.get(shift)!.push(task);
  }

  return Array.from(dateMap.entries()).map(([date, shiftMap]) => {
    const shiftGroups = Array.from(shiftMap.entries()).map(
      ([label, items]) => ({
        label,
        items,
      })
    );

    return { date, shiftGroups };
  });
});

const problemFilterItems = computed(() =>
  (workTypes.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};
const toggleShift = (date: string, shift: string) => {
  const key = `${date}::${shift}`;
  collapsedShifts.value[key] = !collapsedShifts.value[key];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];
const isShiftCollapsed = (date: string, shift: string) =>
  collapsedShifts.value[`${date}::${shift}`];

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("HH:mm:ss");
}

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const getStatus = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const goLink = (e: any, item: any) => {
  e.stopPropagation();
  window.open(item.eld_link, "_blank")?.focus();
};

const getTasks = async () => {
  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");
  const filter: IFilterEligibleTasks = {
    page: filterPage.value,
    per_page: filterPerPage.value,
    search: searchQuery.value,
    organization_id: organizationId,
    shift_id: route.query.shift_id ? route.query.shift_id : "",
    team_id: route.query.team_id ? route.query.team_id : "",
    user_id: route.params.id,
    work_type_id: problem.value,
  };
  await checkStore.getEligibleTasks(loading, filter, (res: any) => {
    if (res) {
      eligibleTasks.value = res.items;
    }
  });
};

const searchFilterWorkType = (val: string) => {
  if (searchFilterProblemTimer) clearTimeout(searchFilterProblemTimer);
  searchFilterProblemTimer = setTimeout(async () => {
    workTypeStore.commonFilter.search = val;
    const [fetchedWorkTypes] = await Promise.all([
      workTypeStore.getWorkTypes(workTypeLoading),
    ]);

    workTypes.value = fetchedWorkTypes;
  }, 400);
};

const getProblems = async () => {
  const [fetchedWorkTypes] = await Promise.all([
    workTypeStore.getWorkTypes(workTypeLoading),
  ]);

  workTypes.value = fetchedWorkTypes;
};

onMounted(async () => {
  if (
    userStore.userProfile?.role === "admin" ||
    userStore.userProfile?.role === "super_admin"
  ) {
    isAdmin.value = true;
  }
  await getTasks();
  await getProblems();
});

watch([searchQuery, problem], () => {
  if (filterTimeout) clearTimeout(filterTimeout);
  filterTimeout = setTimeout(async () => {
    await getTasks();
  }, 300);
});
</script>

<template>
  <div class="v-check-child">
    <div class="v-check-child__header">
      <div class="left">
        <CustomTab :tabs="tabs" v-model="activeTab" />
      </div>

      <div class="right">
        <CommonSearch
          v-model="searchQuery"
          :icon="SearchMd"
          placeholder="Search"
          clearable
        />

        <CustomSearchDropdown
          v-model="problem"
          :items="problemFilterItems"
          label="Problem"
          position="bottom"
          clearable
          dropdownFullWidth
          maxWidth="400px"
          @search="searchFilterWorkType"
        />
      </div>
    </div>

    <div class="v-check-child__body">
      <template
        v-for="(dateGroup, dateIndex) in groupedByDate"
        :key="dateIndex"
      >
        <div
          class="date-row"
          @click="toggleDate(dateGroup.date)"
          :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
        >
          <span class="date-label">{{ dateGroup.date }}</span>
          <span
            class="date-chevron"
            :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
          >
            <ChevronDown :size="20" />
          </span>
        </div>

        <template v-if="!isDateCollapsed(dateGroup.date)">
          <template
            v-for="(shiftGroup, shiftIndex) in dateGroup.shiftGroups"
            :key="shiftIndex"
          >
            <CommonTable
              :headers="checkStore.checkChildHeaders"
              :groups="[
                {
                  label: shiftGroup.label,
                  items: shiftGroup.items,
                },
              ]"
              :loading="loading"
              :collapsed="isShiftCollapsed(dateGroup.date, shiftGroup.label)"
              @toggle-group="toggleShift(dateGroup.date, shiftGroup.label)"
              :dateIndex="dateIndex"
              :shiftIndex="shiftIndex"
              :isAdmin="isAdmin"
              :withoutHeader="shiftIndex > 0"
            >
              <template #id="{ index }">
                <span>{{ shiftGroup.items.length - index }}</span>
              </template>
              <template #company="{ item }">
                <span>
                  {{ item.company_name }}
                </span>
              </template>
              <template #drivers="{ item }">
                <span>{{ item.driver_name }}</span>
              </template>
              <template #users="{ item }">
                <span>{{ item.assignee_name }}</span>
              </template>
              <template #date="{ item }">
                <span>{{ datepickerToUtcDateOnly(item.created_time) }}</span>
              </template>
              <template #status="{ item }">
                <CommonStatus
                  v-if="item.status && getStatus(item.status)"
                  :status="getStatus(item.status)!"
                  :tooltipContent="
                    tooltipContent(item.qa_failed_reason_details)
                  "
                />
                <span v-else>—</span>
              </template>

              <template #check="{ item }">
                <span class="qa-status" :class="item.qa_status">
                  {{ item.qa_status }}
                </span>
              </template>

              <template #problems="{ item }">
                <span>{{ item.work_type_name }}</span>
              </template>

              <template #actions="{ item }">
                <div v-if="item.eld_link" class="icon">
                  <LinkExternal02
                    :size="16"
                    @click="(e: any) => goLink(e, item)"
                  />
                </div>
              </template>
            </CommonTable>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-check-child {
  padding: var(--base-space-24);
  display: flex;
  flex-direction: column;
  gap: var(--base-space-24);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .right {
      display: flex;
      align-items: center;
      justify-content: end;
      flex: 1;
      gap: 12px;
    }
  }

  .date-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--base-space-8);
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    border-top-left-radius: var(--base-radius-16);
    border-top-right-radius: var(--base-radius-16);
    transition: background-color 0.15s;
    background: var(--color-secondary-system-background);
    margin-top: 10px;
    height: 40px;

    &.is-collapsed {
      border-radius: var(--base-radius-16);
    }
  }

  .date-chevron {
    display: flex;
    align-items: center;
    color: var(--color-label-secondary);
    transition: transform 0.2s;

    &.is-collapsed {
      transform: rotate(-180deg);
    }
  }

  .date-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-label);
  }

  :deep(.c-co-table) {
    margin-top: 0 !important;
  }

  :deep(.table thead th) {
    &:first-child {
      border-top-left-radius: 0 !important;
    }

    &:last-child {
      border-top-right-radius: 0 !important;
    }
  }
}

.qa-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border-radius: 8px;

  &.PASSED {
    background: var(--color-green-light);
  }

  &.FAILED {
    background: var(--color-red-light);
  }
}

.icon {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  color: var(--color-primary);
  background: var(--color-secondary-system-background);
  cursor: pointer;
}
</style>
